import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditorGroup, Spacer } from "./EditorHelpers";
import { BoycottHeaderProps } from "./headerTypes";
import { useRestorable, v } from "../util/useRestorable";
import { PresetColorEditor } from "../components/ColorPicker";
import { PresetTextEditor } from "../components/TextEditor";
import { useFlyerConfig } from "../util/useFlyerConfig";
import { countryFromLocale, countryNameByLocale } from "../locales/locales";
import { TFunction } from "i18next";


interface BoycottHeaderEditorProps {
  onChange: (update: BoycottHeaderProps) => void;
}

const getDefaultSmallHeader1 = (dt: TFunction<any, undefined>, flyerLocale: string) => {
  const country = countryFromLocale(flyerLocale);
  if (country === "CA") {
    return dt('default.stronger-canada.title', { x: countryNameByLocale[flyerLocale] }).toUpperCase();
  }
  return dt('default.stronger-europe.title').toUpperCase();
}

export default function BoycottHeaderEditor(props: BoycottHeaderEditorProps) {
  const { t } = useTranslation();
  const { onChange } = props;
  const { flyerLocale } = useFlyerConfig();
  const { t: dt } = useTranslation(null, { lng: flyerLocale.substring(0, 2) })
  const [bigHeader1, setBigHeader1] = useRestorable(dt('default.boycott.title').toUpperCase(), 'bigHeader1');
  const [bigHeader2, setBigHeader2] = useRestorable(dt('default.usa.title').toUpperCase(), 'bigHeader2');
  const [smallHeader1, setSmallHeader1] = useRestorable(
    getDefaultSmallHeader1(dt, flyerLocale),
    'smallHeader1');
  const [smallHeader2, setSmallHeader2] = useRestorable(dt('default.weaker-trump.title').toUpperCase(), 'smallHeader2');
  const [bigColor, setBigColor] = useRestorable("#584932", 'bigColor');
  const [smallColor, setSmallColor] = useRestorable("#3f3424", 'smallColor');
  const [debouncedChange, setDebouncedChange] = useState<BoycottHeaderProps | null>(null);

  // Debounce changes
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedChange({
        bigHeader1: v(bigHeader1),
        bigHeader2: v(bigHeader2),
        smallHeader1: v(smallHeader1),
        smallHeader2: v(smallHeader2),
        bigColor: v(bigColor),
        smallColor: v(smallColor),
      }); // TODO method to strip presets?
    }, 250);

    // Cleanup function to clear the timeout
    return () => {
      clearTimeout(handler);
    };
  }, [bigHeader1, bigHeader2, smallHeader1, smallHeader2, bigColor, smallColor]);

  useEffect(() => {
    onChange(debouncedChange)
  }, [debouncedChange]);

  return (
    <EditorGroup>
      <PresetTextEditor 
        label={t('big-header.label')}
        value={bigHeader1}
        onChange={setBigHeader1} 
      />
      <PresetTextEditor 
        value={bigHeader2}
        onChange={setBigHeader2} 
      />
      <PresetColorEditor 
        value={bigColor}
        onChange={setBigColor} 
      />
      <Spacer />
      <PresetTextEditor 
        label={t('small-header.label')}
        value={smallHeader1}
        onChange={setSmallHeader1} 
      />
      <PresetTextEditor 
        value={smallHeader2}
        onChange={setSmallHeader2} 
      />
      <PresetColorEditor 
        value={smallColor}
        onChange={setSmallColor} 
      />
    </EditorGroup>
  )
}

